/**
 * inlineEditReducer — the state machine behind editing in place.
 *
 * THE DRAFT IS THE THING BEING PROTECTED. Every form on the screen this replaces held its values
 * in `useState` seeded by a `useEffect` on the query result, so a background refetch — and three
 * of these reads are `staleTime: 0` — overwrote whatever an admin had typed, silently, mid-word.
 * These values print onto legal instruments, so a lost keystroke is not a cosmetic bug.
 *
 * The rules, all four of them pinned by tests:
 *
 * 1. A DRAFT SURVIVES A REFETCH. `server` moving underneath a dirty block updates what Cancel
 *    goes back to and what the block compares against — it never touches what is in the field.
 * 2. A FAILED COMMIT KEEPS THE DRAFT AND THE REASON. The server's own sentence is shown where
 *    the admin typed, and the value they typed is still there to correct.
 * 3. CANCEL RESTORES THE SERVER VALUE, whatever the field has been through since.
 * 4. NOTHING AUTOSAVES. `commit` is an explicit transition; `dirty` is the resting state of an
 *    edited block and it never counts as progress anywhere on the page.
 *
 * …and a fifth that only appears once a block is a GROUP standing for an entity you can swap:
 *
 * 5. A DRAFT BELONGS TO THE ENTITY IT WAS TYPED AGAINST. Rule 1 protects a draft from a refetch
 *    of the SAME thing. When the row changes WHICH thing it is about — a different witness picked,
 *    a different policy linked — the values in the fields are about the old one, and carrying them
 *    across would write one person's phone number onto another's record. A `sync` that names a new
 *    `subject` therefore resets the group. The two rules never collide: same subject, keep the
 *    draft; different subject, it was never this entity's draft to keep.
 *
 * …and a sixth that only matters where a group's fields do not all arrive from the same read:
 *
 * 6. AN EMPTY FIELD NOBODY TYPED INTO IS NOT A STATEMENT THAT IT IS EMPTY. Rule 1 is why: a draft
 *    seeded while one of the group's reads was still in flight holds `''` for that read's fields
 *    because nothing has ARRIVED, and rule 1 then keeps that stale empty for as long as the group
 *    stays dirty — including past the moment the read lands with a recorded value. The draft alone
 *    cannot tell that apart from an admin who emptied the field on purpose, and the two want
 *    opposite writes. `touched` records which fields the admin has actually typed into, so the
 *    write can state a value for those and stay silent about the rest.
 *
 * …and a seventh that is about the write in flight rather than about the values:
 *
 * 7. A COMMIT IN FLIGHT OWNS THE GROUP. `saving` is not decoration: it is what the Save button and
 *    the in-flight write are gated on. An `edit` arriving under it — a keystroke in a field still
 *    focused, or a field set programmatically by something that has just read a document — would
 *    put the group back to `dirty`, re-enable Save over a write that has not answered yet, and on
 *    a group whose commit CREATES the row that is a second row for the same thing. The edit is
 *    dropped rather than queued: `committed` or `failed` is what hands the group back, and either
 *    one leaves the fields in a state the admin can see before typing again.
 *
 *    A `cancel` UNDER THE SAME COMMIT IS REFUSED FOR THE SAME REASON, and it arrives by more
 *    routes than a keystroke does: `Esc` anywhere inside the group (`blockKeys`), and every caller
 *    that drops a draft because the row was repointed. Taking the group to `clean` mid-write would
 *    hide the commit bar, lose the spinner, un-gate the fields the write is carrying, and then be
 *    contradicted by the `committed` or `failed` that is still on its way. The group is handed
 *    back by the answer, and a discard pressed a moment later costs one click.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only, and this is the rule set that has
 * to be provable rather than demonstrated.
 */

/** One block's worth of fields. Values are the STRINGS the inputs hold, never parsed numbers. */
export type FieldValues = Record<string, string>;

export type InlineStatus = 'clean' | 'dirty' | 'saving' | 'error';

export interface InlineState {
    /** The values as the server last reported them. Cancel goes back to exactly this. */
    server: FieldValues;
    /** What the inputs are showing. Equal to `server` in `clean`. */
    draft: FieldValues;
    status: InlineStatus;
    /** The server's own refusal, verbatim. Null unless `status === 'error'`. */
    error: string | null;
    /**
     * A field-scoped refusal, where the server named one. The block's own sentence stays in
     * `error`; this puts it under the input that caused it.
     */
    fieldErrors: Record<string, string>;
    /** The provenance line a committed block prints: when it last saved, in this session. */
    savedAt: string | null;
    /**
     * WHICH FIELDS THE ADMIN HAS TYPED INTO since this group was last clean — rule 6.
     *
     * NOT THE SAME QUESTION AS `changedFields`, and the difference is the whole point. That one
     * asks whether the draft differs from the server, which a stale empty seeded before a late read
     * landed also answers yes to. This asks whether a human put the value there, which only an
     * `edit` can make true — so a write that must not guess may ask it instead.
     *
     * EVER-TYPED, NOT CURRENTLY-DIFFERENT. Typing a value back to what the server holds leaves the
     * field touched: the admin has still STATED it, and stating the value the row already carries
     * is a no-op write rather than a wrong one. It empties on the three transitions where nothing
     * typed survives — a commit that landed, a discard, and a change of subject.
     */
    touched: string[];
    /**
     * WHICH ENTITY these values are about, for the groups that stand for one — `witness:41`,
     * `policy:7`. Null for the blocks that are simply the contract's own fields, where there is
     * nothing to swap and rule 5 has nothing to fire on.
     */
    subject: string | null;
}

export type InlineAction =
    /** A keystroke. */
    | { type: 'edit'; field: string; value: string }
    /**
     * The read answered again — with the same values, with different ones, or about somebody
     * else entirely. Omitting `subject` leaves the group's subject where it was, which is what
     * every block that is not standing for a swappable entity does.
     */
    | { type: 'sync'; server: FieldValues; subject?: string | null }
    /** The admin asked for the write. */
    | { type: 'commit' }
    | { type: 'committed'; server: FieldValues; at: string }
    | { type: 'failed'; message: string; fieldErrors?: Record<string, string> }
    /** Discard: back to the server's values. */
    | { type: 'cancel' };

export function initialInlineState(
    server: FieldValues,
    subject: string | null = null,
): InlineState {
    return {
        server,
        draft: server,
        status: 'clean',
        error: null,
        fieldErrors: {},
        savedAt: null,
        subject,
        touched: [],
    };
}

function sameValues(a: FieldValues, b: FieldValues): boolean {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const key of keys) {
        if ((a[key] ?? '') !== (b[key] ?? '')) return false;
    }
    return true;
}

/** True when the block has something to save. The commit bar is drawn from this and nothing else. */
export function isDirty(state: InlineState): boolean {
    return !sameValues(state.draft, state.server);
}

/** The fields that differ, for the hazard dialog's before/after and for the draft banner. */
export function changedFields(state: InlineState): string[] {
    return Object.keys({ ...state.server, ...state.draft }).filter(
        (field) => (state.draft[field] ?? '') !== (state.server[field] ?? ''),
    );
}

/**
 * What the group's commit bar says it covers.
 *
 * THE COMMIT UNIT IS THE GROUP, AND THE BAR HAS TO SAY SO. A bar that appears next to one edited
 * field, over a block holding four, reads as "save this field" — and the write sends all four.
 * Naming the count is the difference between an admin who knows that and one who finds out.
 */
export function commitHint(state: InlineState): string {
    const count = changedFields(state).length;
    return count > 1 ? `${count} fields commit together` : 'One commit for the whole block';
}

/**
 * The eyebrow's `2 fields rejected`, per the quiet-field anatomy.
 *
 * ONLY ABOVE ONE. A single refusal is already legible under the field that caused it, and
 * repeating it in the eyebrow says the block was refused twice. Two or more is the case the
 * eyebrow exists for: the caret is in one field and the other refusal is off the fold.
 */
export function rejectedSummary(state: InlineState): string | undefined {
    const count = Object.keys(state.fieldErrors).length;
    return count > 1 ? `${count} fields rejected` : undefined;
}

export function inlineEditReducer(state: InlineState, action: InlineAction): InlineState {
    switch (action.type) {
        case 'edit': {
            // RULE 7. The write is in the air and the group is gated on it; taking the group back
            // to `dirty` here would offer Save a second time over a commit that has not answered.
            if (state.status === 'saving') return state;

            // A KEYSTROKE CLEARS THE REFUSAL FOR THAT FIELD ONLY. The block-level sentence stays
            // until the next commit answers it: the server refused the whole write, and editing
            // one field is not evidence the rest is now acceptable.
            const draft = { ...state.draft, [action.field]: action.value };
            const fieldErrors = { ...state.fieldErrors };
            delete fieldErrors[action.field];
            const status: InlineStatus = sameValues(draft, state.server) ? 'clean' : 'dirty';
            // RULE 6. The keystroke is the only evidence a value in this field is anybody's
            // statement rather than a placeholder the group was seeded with.
            const touched = state.touched.includes(action.field)
                ? state.touched
                : [...state.touched, action.field];
            return { ...state, draft, status, fieldErrors, touched };
        }

        case 'sync': {
            // RULE 5, BEFORE RULE 1 — the order matters. A group whose subject has changed is not
            // holding a stale draft of this entity; it is holding another entity's, and the only
            // safe thing to do with it is drop it. The refusal and the provenance line go with it:
            // both were about the previous subject too.
            const subject = action.subject === undefined ? state.subject : action.subject;
            if (subject !== state.subject) return initialInlineState(action.server, subject);

            // RULE 1. The server moved; the draft does not. A clean block follows the server (that
            // is what makes another admin's edit show up); a dirty, saving or errored one keeps
            // every character it holds and only learns what Cancel now means.
            if (state.status === 'clean' || !isDirty(state)) {
                return { ...state, server: action.server, draft: action.server };
            }
            return { ...state, server: action.server };
        }

        case 'commit':
            return { ...state, status: 'saving', error: null, fieldErrors: {} };

        case 'committed':
            return {
                server: action.server,
                draft: action.server,
                status: 'clean',
                error: null,
                fieldErrors: {},
                savedAt: action.at,
                subject: state.subject,
                // What was typed has been written and is now the server's own value; nothing in
                // this group is anybody's un-saved statement any more.
                touched: [],
            };

        case 'failed':
            // RULE 2. Verbatim, and the draft is untouched — there is nothing to retype.
            return {
                ...state,
                status: 'error',
                error: action.message,
                fieldErrors: action.fieldErrors ?? {},
            };

        case 'cancel':
            // RULE 7 FIRST. `Esc` and the repoint-the-row callers both land here, and neither can
            // recall the write that has already left with these values.
            if (state.status === 'saving') return state;

            // RULE 3, and rule 6 goes with it: the typing is discarded, so is the record of it.
            return {
                ...state,
                draft: state.server,
                status: 'clean',
                error: null,
                fieldErrors: {},
                touched: [],
            };

        default:
            return state;
    }
}
